import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../editor/chrome/icons';
import { ARTBOARD_PRESETS, PRESET_GROUPS, type PresetInfo } from '../editor/artboardPresets';
import type { BrandCard } from '../services/work';
import { Button, NumberField, Spinner } from '../ui/controls';
import { Menu, useMenu, type MenuEntry } from '../ui/menu';
import { Popover } from '../ui/popover';
import { tip } from '../ui/tooltip';
import { BrandPicker } from './BrandPicker';
import { Dot } from './parts';
import { customSize, fileNameFrom, QUICK_SIZES, shortName, sizeLabel, startSizeOf, type StartSize } from './sizes';
import { tileUrl } from '../editor/content/tileUrl';
import { presetFromWords } from './pagePreset';

/** What the prompt box hands the Home when somebody starts a file. */
export interface Start {
  readonly name: string;
  readonly artboard: StartSize;
  /** The brand the file is made in; absent for none, and inside a project the project's. */
  readonly brandId?: string;
}

/**
 * The sizes a file can start at, as a menu: the six people start from most, then every other
 * preset a submenu away (the whole list at once ran off the bottom of the window), then a
 * size of one's own. The prompt box's size pill and the New File button both show it.
 */
export function sizeMenuItems({ current, onPick, onCustom }: { current?: StartSize; onPick: (size: StartSize) => void; onCustom: () => void }): MenuEntry[] {
  const pick = (preset: PresetInfo): MenuEntry => ({ id: preset.id, label: preset.name, shortcut: preset.says, checked: current?.preset === preset.id, onSelect: () => onPick(startSizeOf(preset)) });
  return [
    ...QUICK_SIZES.map(pick),
    { kind: 'separator', id: 'sep-more' },
    ...PRESET_GROUPS.map((group): MenuEntry => ({
      kind: 'submenu',
      id: `group-${group}`,
      label: group,
      items: ARTBOARD_PRESETS.filter((preset) => preset.group === group && !QUICK_SIZES.includes(preset)).map(pick),
    })),
    { kind: 'separator', id: 'sep-custom' },
    { id: 'custom', label: 'Custom Size…', checked: !!current && !current.preset, onSelect: onCustom },
  ];
}

/** The brand a file starts in, remembered per workspace in this browser: an id, "none", or nothing chosen yet. */
export function useStartBrand(workspace: string) {
  return useRemembered(`artwork.home.brand.${workspace}`);
}

/** The brand in force: the one chosen, or the first there is. "none" is a choice too. */
export function brandInForce(brands: readonly BrandCard[] | null, brandId: string | null): BrandCard | null {
  if (brands === null || brandId === 'none') return null;
  return brands.find((one) => one.id === brandId) ?? brands[0] ?? null;
}

/**
 * The prompt box: "What are we creating today?", with what it will make carried as pills —
 * the size, the brand, and the project when the Home is one project's page.
 *
 * It is where AI will be asked, later — a prompt, so its arrow sends words and waits for them.
 * Until then the arrow starts a file of that size in that brand, named by the words
 * (`fileNameFrom`). A labelled Create inside the box was sent back (Tarek, 2026-10-01: "adding
 * the create button in the prompt bar is wrong"): a blank file is started from the row under
 * it — New File, then the common sizes, one press each (his pick of three places).
 */
export function Composer({
  brands,
  brandId,
  onBrand,
  project,
  busy,
  onStart,
}: {
  /** Every brand this person may use; null while they load. */
  brands: readonly BrandCard[] | null;
  /** The brand chosen for new files (`useStartBrand`), shared with the New File button. */
  brandId: string | null;
  onBrand: (id: string) => void;
  /** Inside a project: files start there, in its brand, so there is no brand to choose. */
  project?: { name: string; color: string | null } | null;
  busy: boolean;
  onStart: (start: Start) => void;
}) {
  const [words, setWords] = useState('');
  const [size, setSize] = useState<StartSize>(() => startSizeOf(QUICK_SIZES[0]!));
  const sizeMenu = useMenu();
  const brandMenu = useMenu();
  const [custom, setCustom] = useState<HTMLElement | null>(null);

  const brand = project ? null : brandInForce(brands, brandId);
  /**
   * Which button started the file being made, so it is that one that says so: a press on a
   * size made a file with no sign of it until the editor opened (QA 2026-10-05, ART-HOM-08).
   */
  const [pressed, setPressed] = useState<string | null>(null);
  const start = (artboard: StartSize, name: string, from: string) => {
    if (busy) return;
    setPressed(from);
    onStart({ name, artboard, ...(brand ? { brandId: brand.id } : {}) });
  };
  // A size the person picked stands; otherwise the words may name the piece ("an Instagram story" → Story, Jev).
  const [sizePicked, setSizePicked] = useState(false);
  const sending = useRef(false);
  const [asking, setAsking] = useState(false);
  const send = async () => {
    if (!words.trim() || busy || sending.current) return;
    sending.current = true;
    setPressed('send');
    setAsking(true);
    try {
      const named = sizePicked ? null : await presetFromWords(words);
      start(named ? startSizeOf(named) : size, fileNameFrom(words), 'send');
    } finally {
      sending.current = false;
      setAsking(false);
    }
  };
  const [blankCustom, setBlankCustom] = useState<HTMLElement | null>(null);

  const sizeItems = sizeMenuItems({ current: size, onPick: (picked) => { setSize(picked); setSizePicked(true); }, onCustom: () => sizeMenu.anchor && setCustom(sizeMenu.anchor) });

  return (
    <div className="mx-auto w-full max-w-[780px]">
      <div data-tour="artwork.composer" className="grid gap-[10px] rounded-[22px] bg-fill-2 pb-[12px] pl-[20px] pr-[12px] pt-[16px] shadow-[inset_0_0_0_1px_var(--separator)] transition-shadow focus-within:shadow-[inset_0_0_0_1px_var(--label-4),0_0_0_4px_var(--accent-soft)]">
        <textarea
          aria-label="What are we creating today?"
          placeholder="What are we creating today?"
          rows={2}
          value={words}
          onChange={(event) => setWords(event.target.value)}
          onKeyDown={(event) => {
            // Return sends, as a message box does; Shift-Return is a new line.
            if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
            event.preventDefault();
            void send();
          }}
          className="min-h-[52px] w-full resize-none bg-transparent pr-[8px] text-[19px] leading-[26px] text-label outline-none placeholder:text-label-3"
        />
        <div className="flex items-center gap-[6px]">
          <Pill buttonRef={sizeMenu.ref} onClick={sizeMenu.toggle} label="Size" open={sizeMenu.open}>
            <Shape width={size.width} height={size.height} />
            {sizeLabel(size)}
          </Pill>
          {project ? (
            <span className="flex h-[32px] items-center gap-[7px] rounded-full bg-fill-1 px-[12px] text-[12.5px] font-medium">
              {project.name}
            </span>
          ) : brands && brands.length > 0 ? (
            <Pill buttonRef={brandMenu.ref} onClick={brandMenu.toggle} label="Brand" open={brandMenu.open}>
              {brand ? <BrandMark brand={brand} /> : null}
              {brand ? brand.name : 'No Brand'}
            </Pill>
          ) : null}
          <button
            type="button"
            aria-label="Start"
            {...tip('Start')}
            disabled={busy || !words.trim()}
            onClick={() => void send()}
            className={`ml-auto grid size-[34px] place-items-center rounded-full transition-colors ${words.trim() ? 'bg-accent text-on-accent hover:bg-accent-hover' : 'bg-fill-1 text-label-3'}`}
          >
            {/* A send arrow of its own: Back turned a quarter read as an arrow until the Nova
                set made Back a bare chevron, and the button then showed ^ (Farah, 2026-10-23). */}
            {(busy || asking) && pressed === 'send' ? <Spinner size={14} /> : <Icon name="arrow-up" size={15} />}
          </button>
        </div>
      </div>
      {/* A blank file is started here, under the box and never inside it: New File first (every
          size a menu away), then the sizes people start from most, one press each. */}
      <div data-tour="artwork.newFile" className="mt-[16px] flex flex-wrap items-center justify-center gap-[6px]">
        <NewFileButton
          busy={busy}
          brandId={brand?.id}
          onStart={(blank) => {
            setPressed('new');
            onStart(blank);
          }}
          className="mr-[6px]"
        />
        {QUICK_SIZES.map((preset) => (
          <button
            key={preset.id}
            type="button"
            disabled={busy}
            {...tip(preset.says)}
            onClick={() => start(startSizeOf(preset), 'Untitled', preset.id)}
            className="flex h-[32px] items-center gap-[7px] rounded-full pl-[11px] pr-[13px] text-[12.5px] font-medium text-label-2 shadow-[inset_0_0_0_1px_var(--separator)] transition-colors hover:bg-fill-2 hover:text-label"
          >
            <Shape width={preset.width} height={preset.height} />
            {shortName(preset)}
            {busy && pressed === preset.id ? <Spinner size={11} /> : null}
          </button>
        ))}
        <button
          type="button"
          disabled={busy}
          onClick={(event) => setBlankCustom(event.currentTarget)}
          className="flex h-[32px] items-center gap-[6px] rounded-full pl-[10px] pr-[13px] text-[12.5px] font-medium text-label-2 shadow-[inset_0_0_0_1px_var(--separator)] transition-colors hover:bg-fill-2 hover:text-label"
        >
          <Icon name="plus" size={12} />
          Custom
          {busy && pressed === 'custom' ? <Spinner size={11} /> : null}
        </button>
      </div>
      <Menu open={sizeMenu.open} anchor={sizeMenu.anchor} onClose={sizeMenu.close} label="Size" items={sizeItems} variant="screen" current={size.preset} />
      <BrandPicker open={brandMenu.open} anchor={brandMenu.anchor} onClose={brandMenu.close} brands={brands ?? []} current={brand?.id ?? null} onPick={onBrand} />
      {custom ? (
        <CustomSize
          anchor={custom}
          action="Use Size"
          onClose={() => setCustom(null)}
          onDone={(width, height) => {
            setCustom(null);
            setSize(customSize(width, height));
            setSizePicked(true);
          }}
        />
      ) : null}
      {blankCustom ? (
        <CustomSize
          anchor={blankCustom}
          action="Start"
          onClose={() => setBlankCustom(null)}
          onDone={(width, height) => {
            setBlankCustom(null);
            start(customSize(width, height), 'Untitled', 'custom');
          }}
        />
      ) : null}
    </div>
  );
}

function Pill({ children, label, open, onClick, buttonRef }: { children: ReactNode; label: string; open: boolean; onClick: () => void; buttonRef: (element: HTMLElement | null) => void }) {
  return (
    <button
      ref={buttonRef}
      type="button"
      aria-label={label}
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={onClick}
      className="flex h-[32px] items-center gap-[7px] rounded-full bg-fill-1 pl-[11px] pr-[10px] text-[12.5px] font-medium hover:bg-fill-selected"
    >
      {children}
      <span className="text-label-3">
        <Icon name="chevron" size={10} />
      </span>
    </button>
  );
}

/** A page of this shape, as a small outline: what the size is, before its name is read. */
function Shape({ width, height }: { width: number; height: number }) {
  const k = 12 / Math.max(width, height);
  return <span aria-hidden className="block shrink-0 rounded-[1.5px] shadow-[inset_0_0_0_1.5px_currentColor]" style={{ width: Math.max(4, Math.round(width * k)), height: Math.max(4, Math.round(height * k)) }} />;
}

/** A brand's logo on a small white chip, the way logos are shown elsewhere on the page. */
function BrandMark({ brand }: { brand: BrandCard }) {
  const [broken, setBroken] = useState(false);
  if (!brand.logoUrl || broken) return <Dot colour="var(--accent)" />;
  return (
    <span className="grid h-[17px] w-[30px] place-items-center overflow-hidden rounded-[5px] bg-white">
      <img src={tileUrl(brand.logoUrl)} alt="" draggable={false} onError={() => setBroken(true)} className="max-h-[12px] max-w-[24px] object-contain" />
    </span>
  );
}

/** Width and height, in pixels, for a size none of the presets is. */
export function CustomSize({ anchor, action, align = 'start', onClose, onDone }: { anchor: HTMLElement; action: string; align?: 'start' | 'end'; onClose: () => void; onDone: (width: number, height: number) => void }) {
  const [width, setWidth] = useState(1080);
  const [height, setHeight] = useState(1080);
  return (
    <Popover open anchor={anchor} onClose={onClose} label="Custom size" placement="below" align={align}>
      <form
        className="grid w-[220px] gap-[10px] p-[12px]"
        onSubmit={(event) => {
          event.preventDefault();
          onDone(width, height);
        }}
      >
        <div className="text-[12px] font-semibold text-label-2">Custom size, in pixels</div>
        <div className="flex gap-[8px]">
          <NumberField label="Width" prefix="W" value={width} min={1} max={100000} decimals={0} onChange={setWidth} className="flex-1" />
          <NumberField label="Height" prefix="H" value={height} min={1} max={100000} decimals={0} onChange={setHeight} className="flex-1" />
        </div>
        <Button type="submit" variant="prominent" size="small">
          {action}
        </Button>
      </form>
    </Popover>
  );
}

/** Told to every reader in this page when one of them changes a remembered choice (the Settings window does, beside Home). */
const REMEMBERED = 'artwork:remembered';

/** A small choice kept in this browser for next time; never needed, so a refusal is ignored. */
export function useRemembered(key: string): [string | null, (value: string) => void] {
  const read = () => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  };
  const [value, setValue] = useState<string | null>(read);
  const keyRef = useRef(key);
  useEffect(() => {
    if (keyRef.current === key) return;
    keyRef.current = key;
    setValue(read());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => {
    const follow = () => setValue(read());
    window.addEventListener(REMEMBERED, follow);
    return () => window.removeEventListener(REMEMBERED, follow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return [
    value,
    (next: string) => {
      setValue(next);
      try {
        localStorage.setItem(key, next);
      } catch {
        // A private window can refuse storage: the choice holds for this visit.
      }
      window.dispatchEvent(new Event(REMEMBERED));
    },
  ];
}

/**
 * "Make a file": pressed, it asks what size and starts a blank file at the size chosen. It
 * leads the sizes under the prompt box, and stands alone where there is no box (All Files).
 */
export function NewFileButton({ busy, brandId, onStart, className = '' }: { busy: boolean; /** The brand a file starts in here, if any. */ brandId?: string; onStart: (start: Start) => void; className?: string }) {
  const menu = useMenu();
  const [custom, setCustom] = useState<HTMLElement | null>(null);
  /** Whether the file being made was started here, so this button says it is coming. */
  const [mine, setMine] = useState(false);
  useEffect(() => {
    if (!busy) setMine(false);
  }, [busy]);
  const start = (artboard: StartSize) => {
    if (busy) return;
    setMine(true);
    onStart({ name: 'Untitled', artboard, ...(brandId ? { brandId } : {}) });
  };
  return (
    <>
      <button
        ref={menu.ref}
        type="button"
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={menu.open}
        onClick={menu.toggle}
        className={`flex h-[32px] shrink-0 items-center gap-[6px] rounded-full bg-accent pl-[11px] pr-[10px] text-[13px] font-semibold text-on-accent transition-colors hover:bg-accent-hover ${busy && mine ? '' : 'disabled:opacity-40'} ${className}`}
      >
        <Icon name="plus" size={14} />
        New File
        {busy && mine ? (
          <Spinner size={11} />
        ) : (
          <span className="opacity-70">
            <Icon name="chevron" size={10} />
          </span>
        )}
      </button>
      <Menu
        open={menu.open}
        anchor={menu.anchor}
        onClose={menu.close}
        label="New File"
        variant="screen"
        items={sizeMenuItems({ onPick: start, onCustom: () => menu.anchor && setCustom(menu.anchor) })}
      />
      {custom ? (
        <CustomSize
          anchor={custom}
          action="Start"
          onClose={() => setCustom(null)}
          onDone={(width, height) => {
            setCustom(null);
            start(customSize(width, height));
          }}
        />
      ) : null}
    </>
  );
}
